import { findNodesForRemoval, findNewNodes } from '@/utilities/d3network.helper'
import {
  AgentStateEnum,
  HostStatusEnum
} from '@/constants/host-status.constants'
import { RiskLevelEnum } from '@/constants/risk-level.constants'

import { AGENT_LIST_REQ } from '@/store/modules/agents/agents.type'

export default {
  data () {
    return {
      networkHosts: {
        nodes: {},
        links: {}
      },
      networkHostsReqStatus: 'loading'
    }
  },
  methods: {
    showAgentTooltip (row, event) {
      // show agent tooltip
      let nodeEl = document.querySelector(`circle[id][id='${row.agentId}']`)
      let agent = _.find(this.networkHosts.nodes, {
        agentId: row.agentId
      })
      this.$refs.agentNetwork.showTooltip(nodeEl, agent, -5, 10)
    },
    hideAgentTooltip (row, event) {
      this.$refs.agentNetwork.hideTooltip(event)
    },
    findMinMaxScore (agents) {
      let minScore = _.minBy(agents, 'aggregatedRiskScore')
      let maxScore = _.maxBy(agents, 'aggregatedRiskScore')
      return { min: minScore, max: maxScore }
    },
    buildNode (agent, minScore, maxScore, ignoreScore = false, cssClass) {
      agent._cssClass = cssClass || ''
      agent.id = agent.agentId
      agent._size = agent.getNodeSize()
      agent._svgAttrs = { id: agent.agentId }

      if (agent.agentStatus === AgentStateEnum.OFFLINE) {
        agent._color = HostStatusEnum.properties.offline.color
      } else if (ignoreScore) {
        agent._color = HostStatusEnum.properties.safe.color
      } else if (agent.aggregatedRiskScore > 0) {
        // Color according to host risk level if host aggregatedRiskScore > 0
        if (agent.riskLevel.level === RiskLevelEnum.HIGH) {
          agent._color = RiskLevelEnum.properties.high.color
        } else if (agent.riskLevel.level === RiskLevelEnum.MEDIUM) {
          agent._color = RiskLevelEnum.properties.medium.color
        } else {
          agent._color = RiskLevelEnum.properties.low.color
        }
      } else {
        agent._color = HostStatusEnum.properties.safe.color
      }
      return agent
    },
    buildLink (movement, agents) {
      if (
        !_.some(agents, { agentId: movement.sourceAgent }) ||
        !_.some(agents, { agentId: movement.targetAgent })
      ) {
        return undefined
      }

      movement.sid = movement.sourceAgent
      movement.tid = movement.targetAgent
      movement._color = 'rgb(167, 168, 170)'
      return movement
    },
    getUniqueLinks (movements) {
      // .uniqWith((a, b) => {
      //   return a.sid !== b.sid && a.tid !== b.sid
      // })
      let uniqLinks = []
      movements.forEach(movement => {
        if (
          !_.find(uniqLinks, link => {
            return (
              link.sourceAgent === movement.sourceAgent &&
              link.targetAgent === movement.targetAgent
            )
          })
        ) {
          uniqLinks.push(movement)
        }
      })
      return uniqLinks
    },
    getNetworkLinks (movements, agents) {
      return Object.values(
        _.chain(this.getUniqueLinks(movements))
          .map(movement => {
            return this.buildLink(movement, agents)
          })
          .omitBy(_.isUndefined)
          .value()
      )
    },
    mapAgentLinks (agents, movements, ignoreScore = false) {
      this.networkHostsReqStatus = 'loading'
      // nodes: [{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }],
      // links: [{ sid: 1, tid: 2 }, { sid: 4, tid: 3 }]
      let minMaxScore = this.findMinMaxScore(agents)
      // agents are the nodes, lateral movements are links
      this.networkHosts = {
        // removing shallow copy for now (use deep copy if needed)
        // mutating hosts with new properties has not effect
        nodes: _.map(agents, agent => {
          return this.buildNode(
            agent,
            minMaxScore.min,
            minMaxScore.max,
            ignoreScore
          )
        }),
        links: [] // links will not be mapped by default
      }
      this.networkHostsReqStatus = 'saved'
    },
    /**
     * Mapping updated node info should not affect current nodelist indexes.
     * 1. Find all existing nodes to be updated and update those nodes.
     * 2. Append any new nodes to the end of the existing nodelist.
     * 3. Remove nodes not found in new nodelist.
     *
     * @param {} agents
     * @param {*} movements
     * @param {*} ignoreScore
     */
    mapUpdatedAgentInfo (agents, movements, ignoreScore) {
      let minMaxScore = this.findMinMaxScore(agents)
      let removedNodes = findNodesForRemoval(
        agents,
        this.networkHosts.nodes,
        'agentId'
      )
      let newNodes = findNewNodes(agents, this.networkHosts.nodes, 'agentId', [
        '_cssClass'
      ])

      // update existing node info {size, color, riskscore}
      newNodes.updated.forEach(node => {
        let newNode = this.buildNode(
          node.obj,
          minMaxScore.min,
          minMaxScore.max,
          ignoreScore,
          node.obj._cssClass
        )
        if (
          this.networkHosts.nodes[node.index]._size !== newNode._size ||
          this.networkHosts.nodes[node.index]._color !== newNode._color
        ) {
          this.$set(
            this.networkHosts.nodes,
            node.index,
            _.assignIn(this.networkHosts.nodes[node.index], {
              _size: newNode._size,
              _color: newNode._color,
              aggregatedRiskScore: newNode.aggregatedRiskScore
            })
          )
        } else {
          newNode.display = this.networkHosts.nodes[node.index].display
          _.assignIn(this.networkHosts.nodes[node.index], newNode)
        }
      })

      // append new nodes to the end of the list
      newNodes.added.forEach(insertIdx => {
        let newNode = this.buildNode(
          insertIdx.newObject,
          minMaxScore.min,
          minMaxScore.max,
          ignoreScore
        )
        this.networkHosts.nodes.splice(insertIdx.index, 0, newNode)
      })

      // remove node (removal index is exclusive of new node indexes)
      removedNodes.forEach(removalIdx => {
        this.networkHosts.nodes.splice(removalIdx, 1)
      })
    },
    getAgentListInfo (ignoreScore = false) {
      this.$store.dispatch(AGENT_LIST_REQ).then(agents => {
        agents.models.sort((a, b) => {
          return a.agentId - b.agentId
        })
        this.mapAgentLinks(
          agents.models,
          this.uniqComboLateralMovements,
          ignoreScore
        )
      })
    },
    updateAgentInfo (ignoreScore = false) {
      this.$store.dispatch(AGENT_LIST_REQ).then(agents => {
        this.mapUpdatedAgentInfo(
          agents.models,
          this.uniqComboLateralMovements,
          ignoreScore
        )
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/agentInfoMixin.js